import React from 'react';
import type { ChatMessage as ChatMessageType } from '../../store/chatBotSlice';
import { BotMessageSquare } from 'lucide-react';

interface ChatMessageProps {
  message: ChatMessageType;
  isLast?: boolean;
  isGreeting?: boolean;
  onRetry?: (messageId: string) => void;
  onCreateTicket?: (sessionId: string) => void;
}


export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  isLast = false,
  // isGreeting = false,  // COMMENTED OUT: Not used while create ticket button is hidden
  onRetry,
  // onCreateTicket  // COMMENTED OUT: Will handle ticket creation automatically in backend
}) => {
  // ✅ Changed from 'sender' to 'role'
  const isUser = message.role === 'user';
  const isSystem = message.role === 'system';
  const isAssistant = message.role === 'assistant';  // ✅ Changed from 'agent' to 'assistant'

  // Simple markdown-like rendering for system messages
  const renderMarkdownContent = (content: string) => {
    // Split by lines and process
    const lines = content.split('\n');
    return lines.map((line, idx) => {
      // Bold text
      line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

      // Links
      line = line.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #0066cc; text-decoration: none;">$1</a>');

      return <div key={idx} dangerouslySetInnerHTML={{ __html: line }} />;
    });
  };

  // Clean and extract actual message from JSON-wrapped responses
  const cleanMessageContent = (content: string): string => {
    // Remove markdown code blocks (```json ... ``` or ``` ... ```)
    let cleanedContent = content.replace(/```json\s*\n?/gi, '').replace(/```\s*$/g, '').trim();

    // Check if the message contains JSON wrapper like {"response": "..."}
    try {
      // Try to parse if it looks like JSON
      if (cleanedContent.trim().startsWith('{') && cleanedContent.trim().endsWith('}')) {
        const parsed = JSON.parse(cleanedContent);
        // If it has a 'response' field, use that
        if (parsed.response) {
          return parsed.response;
        }
      }
    } catch (e) {
      // If parsing fails, return the content without code blocks
      return cleanedContent;
    }

    // Return original content if not JSON
    return content.includes('```') ? cleanedContent : content;
  };

  // Render message content with clickable email links
  const renderMessageContent = (content: string) => {
    // Clean the content first (remove JSON wrapper if present)
    const cleanedContent = cleanMessageContent(content);

    // Email regex pattern
    const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;
    const parts = cleanedContent.split(emailRegex);

    return parts.map((part, index) => {
      // Check if this part is an email
      if (emailRegex.test(part)) {
        return (
          <a
            key={index}
            href={`mailto:${part}`}
            className="email-link"
            style={{
              color: '#0066cc',
              textDecoration: 'none',
              fontWeight: '500'
            }}
          >
            {part}
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  const formatTime = (timestamp: Date) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getStatusIcon = () => {
    switch (message.status) {
      case 'sending':
        return (
          <div className="">
          </div>
        );
      case 'sent':
        return (
          <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'delivered':
        return (
          <svg className="w-3 h-3 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'failed':
        return (
          <svg className="w-3 h-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getMessageTypeIcon = () => {
    switch (message.messageType) {
      case 'error':
        return (
          <svg className="w-4 h-4 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'info':
        return (
          <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  // System messages styling
  if (isSystem) {
    return (
      <div className="system-message">
        <div className="system-message-content">
          {getMessageTypeIcon()}
          <div className="system-message-text">
            {renderMarkdownContent(message.content)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        padding: '6px 12px',
        marginBottom: '8px'
      }}
    >
      <div 
        style={{
          display: 'flex',
          flexDirection: isUser ? 'row' : 'row',
          alignItems: 'flex-end',
          gap: '8px',
          maxWidth: '85%'
        }}
      >
        
        {/* Avatar */}
        {!isUser && (
          <div
            style={{
              width: '36px',
              height: '36px',
              backgroundColor: '#ffffff',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginBottom: '4px',
              border: '2px solid #C1E4FB'
            }}
          >
            <BotMessageSquare size={20} color="#0f3340" strokeWidth={2} />
          </div>
        )}

        {/* Message Bubble */}
        <div 
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isUser ? 'flex-end' : 'flex-start'
          }}
        >
          
          {/* Message Content */}
          <div
            style={{
              padding: '12px 16px',
              borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              maxWidth: '280px',
              wordWrap: 'break-word',
              background: 'linear-gradient(180deg, #FFFFFF 0%, #F9FDFF 50%, #F5FBFE 100%)',
              color: '#2C3E50',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(193, 228, 251, 0.4)',
              opacity: message.status === 'failed' ? 0.7 : 1,
              cursor: 'default',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              {!isUser && getMessageTypeIcon()}
              <div style={{ flex: 1 }}>
                <p style={{
                  fontSize: '14px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  margin: 0,
                  fontWeight: '400',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}>
                  {renderMessageContent(message.content)}
                </p>

                {/* Ticket Link Icon - Show if ticketUrl is in context */}
                {isAssistant && message.context?.ticketUrl && (
                  <a
                    href={message.context.ticketUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ticket-link-button"
                    title="View ticket in Freshdesk"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12,19L8,15H10.5V12H13.5V15H16L12,19Z"/>
                    </svg>
                    View Ticket
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Message Footer */}
          <div className={`flex items-center space-x-2 mt-1 px-2 ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
            
            {/* Timestamp */}
            <span className="text-xs text-gray-500 group-hover:opacity-100 opacity-0 transition-opacity duration-200">
              {formatTime(message.timestamp)}
            </span>

            {/* Status Indicator (for user messages) */}
            {isUser && (
              <div className="flex items-center space-x-1">
                {getStatusIcon()}
              </div>
            )}

            {/* Retry Button (for failed messages) */}
            {message.status === 'failed' && onRetry && (
              <button
                onClick={() => onRetry(message.id)}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(193, 228, 251, 0.3)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  padding: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(193, 228, 251, 0.5)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(193, 228, 251, 0.3)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title="Retry sending message"
              >
                <svg
                  style={{ width: '14px', height: '14px', color: '#0f3340' }}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}
          </div>

          {/* Processing Time (for assistant responses) */}
          {isAssistant && message.context?.processingTimeMs && isLast && (
            <div className="text-xs text-gray-400 mt-1 px-2">
              Responded in {Math.round(message.context.processingTimeMs / 1000 * 10) / 10}s
            </div>
          )}

          {/* Create Support Ticket Button - COMMENTED OUT: Will handle ticket creation automatically in backend when session closes
          {isAssistant && !isGreeting && onCreateTicket &&
           !message.content.includes('Ticket Created') &&
           !message.content.includes('Ticket Creation Issue') && (
            <div className="message-footer">
              <button
                onClick={() => onCreateTicket(message.sessionId)}
                className="btn-create-ticket-small"
                title="Need more help? Create a support ticket"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
                </svg>
                Create Support Ticket
              </button>
            </div>
          )}
          */}
        </div>

        {/* User Avatar */}
        {isUser && (
          <div
            style={{
              width: '36px',
              height: '36px',
              backgroundColor: '#ffffff',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginBottom: '4px',
              border: '2px solid #C1E4FB'
            }}
          >
            <svg
              style={{ width: '20px', height: '20px', color: '#4A4A7A' }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;